"use client";

import React, { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CardDescription, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { KeywordFieldsProps } from "../../types/keyword.types";
import {
  handleSelectAll,
  handleClearAllFields,
  isFieldSelected,
  isFieldIndeterminate,
  toggleField,
  handleSubFieldChange,
} from "../../utils/stepperUtils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

export const KeywordFields = React.memo(function KeywordFields({
  platformsSelected,
  data: fieldsData,
  showAllWhenEmpty = false,
  title,
  description,
  tabs = false,
  activeTab: externalActiveTab,
  onTabChange,
  selectedSubFields,
  setSelectedSubFields,
}: KeywordFieldsProps) {
  const [expandedFields, setExpandedFields] = React.useState<string[]>([
    ...Object.keys(fieldsData),
  ]);
  const [internalActiveTab, setInternalActiveTab] = React.useState("print");
  const activeTab = externalActiveTab || internalActiveTab;

  const filteredFieldsData = useMemo(() => {
    if (platformsSelected.length === 0) {
      if (showAllWhenEmpty) return fieldsData;
      return Object.fromEntries(
        Object.entries(fieldsData).filter(([field]) => field === "general"),
      );
    }
    const keys = ["general", ...platformsSelected];
    return Object.fromEntries(
      Object.entries(fieldsData).filter(([field]) => keys.includes(field)),
    );
  }, [platformsSelected, fieldsData, showAllWhenEmpty]);

  const organizeFieldsByCategory = useMemo(() => {
    if (!tabs) return null;
    return {
      print: Object.fromEntries(
        Object.entries(filteredFieldsData).filter(([key]) =>
          ["newspapers", "tabloids", "provincial", "magazines"].includes(key),
        ),
      ),
      online: Object.fromEntries(
        Object.entries(filteredFieldsData).filter(
          ([key]) => key === "social-media",
        ),
      ),
      tv: Object.fromEntries(
        Object.entries(filteredFieldsData).filter(
          ([key]) => key === "tv-channels",
        ),
      ),
      radio: Object.fromEntries(
        Object.entries(filteredFieldsData).filter(([key]) =>
          ["am-channels", "fm-channels"].includes(key),
        ),
      ),
    };
  }, [filteredFieldsData, tabs]);

  const fieldSelectionState = useMemo(() => {
    const stateMap: Record<
      string,
      { selected: boolean; indeterminate: boolean }
    > = {};
    for (const [field] of Object.entries(filteredFieldsData)) {
      stateMap[field] = {
        selected: isFieldSelected(field, filteredFieldsData, selectedSubFields),
        indeterminate: isFieldIndeterminate(
          field,
          filteredFieldsData,
          selectedSubFields,
        ),
      };
    }
    return stateMap;
  }, [filteredFieldsData, selectedSubFields]);

  const handleFieldToggle = useCallback(
    (field: string) => {
      toggleField(field, setExpandedFields);
    },
    [setExpandedFields],
  );

  const handleFieldCheckedChange = useCallback(
    (
      field: string,
      data: { subFields: string[] },
      checked: boolean | "indeterminate",
    ) => {
      if (checked) {
        if (!expandedFields.includes(field)) {
          setExpandedFields((prev) => [...prev, field]);
        }
        setSelectedSubFields((prev) => [
          ...new Set([...prev, ...data.subFields]),
        ]);
      } else {
        setSelectedSubFields((prev) =>
          prev.filter((sf) => !data.subFields.includes(sf)),
        );
      }
    },
    [expandedFields, setExpandedFields, setSelectedSubFields],
  );

  const handleSubFieldToggle = useCallback(
    (subField: string, checked: boolean) => {
      handleSubFieldChange(
        subField,
        checked,
        selectedSubFields,
        setSelectedSubFields,
      );
    },
    [selectedSubFields, setSelectedSubFields],
  );

  const renderFieldsList = (fieldsToRender: typeof filteredFieldsData) =>
    Object.entries(fieldsToRender).map(([field, data]) => {
      const isExpanded = expandedFields.includes(field);

      return (
        <div key={field} className="flex flex-col space-y-4">
          <div className="flex items-center border-gray-200 bg-transparent">
            <Checkbox
              id={field}
              checked={fieldSelectionState[field]?.selected}
              indeterminate={fieldSelectionState[field]?.indeterminate}
              onCheckedChange={(checked) =>
                handleFieldCheckedChange(field, data, checked)
              }
              className="mr-3 flex-shrink-0"
            />
            <div className="flex w-full min-w-0 items-center justify-between">
              <button
                onClick={() => handleFieldToggle(field)}
                className="flex min-w-0 flex-1 items-center text-left"
                type="button"
              >
                <span className="text-md flex-shrink-0 cursor-pointer pr-2 font-bold text-neutral-800">
                  {data.label}
                </span>
                <div className="h-0.5 flex-1 bg-neutral-200" />
              </button>
              <div className="ml-2 flex-shrink-0 items-center justify-center">
                {isExpanded ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleFieldToggle(field)}
                        className="p-1"
                        type="button"
                      >
                        <i className="ri-arrow-up-s-fill cursor-pointer text-lg text-neutral-800 transition-colors duration-200 hover:text-neutral-900"></i>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Collapse</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleFieldToggle(field)}
                        className="p-1"
                        type="button"
                      >
                        <i className="ri-arrow-down-s-fill cursor-pointer text-lg text-neutral-800 transition-colors duration-200 hover:text-neutral-900"></i>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Expand</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {isExpanded && (
            <div className="pl-6 sm:pl-10">
              {/* Simpler responsive grid using Tailwind */}
              <div
                className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {data.subFields.map((subField: string) => (
                  <div
                    key={subField}
                    className="flex min-w-0 items-center space-x-2"
                  >
                    <Checkbox
                      id={subField}
                      checked={selectedSubFields.includes(subField)}
                      onCheckedChange={(checked) =>
                        handleSubFieldToggle(subField, checked as boolean)
                      }
                      className="flex-shrink-0"
                    />
                    <Label
                      htmlFor={subField}
                      className="cursor-pointer truncate text-sm font-semibold text-neutral-800"
                    >
                      {subField}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex-shrink-0">
        <div className="flexcol flex w-full items-center gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="leading-normal">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-nowrap">
            <Button
              onClick={() =>
                tabs && organizeFieldsByCategory
                  ? (() => {
                      const currentTabData =
                        organizeFieldsByCategory[
                          activeTab as keyof typeof organizeFieldsByCategory
                        ];
                      const currentTabSubFields = Object.values(
                        currentTabData,
                      ).flatMap((f) => f.subFields);
                      setSelectedSubFields((prev) => [
                        ...new Set([...prev, ...currentTabSubFields]),
                      ]);
                      setExpandedFields((prev) => [
                        ...new Set([...prev, ...Object.keys(currentTabData)]),
                      ]);
                    })()
                  : handleSelectAll(
                      filteredFieldsData,
                      setExpandedFields,
                      setSelectedSubFields,
                    )
              }
              variant="minimal"
            >
              {tabs ? `Select All ${activeTab}` : "Select All"}
            </Button>
            <Button
              onClick={() =>
                tabs && organizeFieldsByCategory
                  ? (() => {
                      const currentTabData =
                        organizeFieldsByCategory[
                          activeTab as keyof typeof organizeFieldsByCategory
                        ];
                      const allSubFields = Object.values(
                        currentTabData,
                      ).flatMap((f) => f.subFields);
                      setSelectedSubFields((prev) =>
                        prev.filter((sf) => !allSubFields.includes(sf)),
                      );
                    })()
                  : handleClearAllFields(setSelectedSubFields)
              }
              variant="destructive"
            >
              {tabs ? `Clear ${activeTab}` : "Clear Fields"}
            </Button>
          </div>
        </div>

        {tabs && organizeFieldsByCategory && (
          <div className="mt-5 flex flex-col gap-1">
            <Tabs
              defaultValue="print"
              value={activeTab}
              onValueChange={onTabChange || setInternalActiveTab}
            >
              <TabsList>
                <TabsTrigger value="print">Print</TabsTrigger>
                <TabsTrigger value="online">Online</TabsTrigger>
                <TabsTrigger value="tv">TV</TabsTrigger>
                <TabsTrigger value="radio">Radio</TabsTrigger>
              </TabsList>
              <TabsContent value="print">
                {renderFieldsList(organizeFieldsByCategory.print)}
              </TabsContent>
              <TabsContent value="online">
                {renderFieldsList(organizeFieldsByCategory.online)}
              </TabsContent>
              <TabsContent value="tv">
                {renderFieldsList(organizeFieldsByCategory.tv)}
              </TabsContent>
              <TabsContent value="radio">
                {renderFieldsList(organizeFieldsByCategory.radio)}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {!tabs && (
        <div className="flex flex-1 flex-col">
          <div className="flex-1 space-y-4">
            {renderFieldsList(filteredFieldsData)}
          </div>
        </div>
      )}
    </div>
  );
});
